import { BaseService } from './base';
import { Roster, RosterEmployeeDefault } from '../models/roster';
import { EmployeeBreak } from '../models/employeeBreak';

import { ApiRequest } from '../lib/request';
import { ApiResponse } from '../lib/response';

import type { Employee } from '../models/employee';

export class RosterService extends BaseService<RosterEmployeeDefault> {
  override model = RosterEmployeeDefault;
  override modelName = 'roster';
  override modelLabel = 'Roster';
  override modelApiVersion = 2;

  /**
   * Returns boolean success from the API response.
   * Pattern from AuthService.requestSuccess.
   */
  private async requestSuccess(request: ApiRequest): Promise<boolean> {
    const response = await this.request(request);

    if (response.success) {
      return response.success;
    } else {
      throw response.errors;
    }
  }

  /**
   * Fetch the default roster configuration for an employee.
   * GET /api/v2/roster/employeeDefault?employee={id}
   */
  async employeeDefault(employee: Employee): Promise<ApiResponse<RosterEmployeeDefault>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/employeeDefault`,
      query: {
        employee: employee.idEmployee.toString(),
      },
    });

    return await this.requestItem<RosterEmployeeDefault>(request, RosterEmployeeDefault);
  }

  /**
   * Save the default roster configuration for an employee.
   * POST /api/v2/roster/saveEmployeeDefault?employee={id}
   */
  async saveEmployeeDefault(
    employee: Employee,
    employeeDefault: RosterEmployeeDefault
  ): Promise<boolean> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/saveEmployeeDefault`,
      query: {
        employee: employee.idEmployee.toString(),
      },
      data: employeeDefault.toJSON(),
    });

    return await this.requestSuccess(request);
  }

  /**
   * Fetch the roster week view.
   * GET /api/v2/roster/week?date={date}
   */
  async week(date: string): Promise<ApiResponse<Roster>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/week`,
      query: { date },
    });

    return await this.requestItem<Roster>(request, Roster);
  }

  /**
   * Fetch the roster month view.
   * POST /api/v2/roster/month?date={date}
   */
  async month(date: string, employees?: (string | number)[]): Promise<ApiResponse<Roster>> {
    const data: Record<string, any> = {};

    if (employees) {
      data.employees = employees;
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/month`,
      query: { date },
      data,
    });

    return await this.requestItem<Roster>(request, Roster);
  }

  /**
   * Save a roster date for an employee.
   * POST /api/v2/roster/saveDate?employee={id}&date={date}
   */
  async saveDate(
    idEmployee: number | string,
    date: any,
    day: any
  ): Promise<ApiResponse<any>> {
    const dateValue = date?.value ?? date;
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/saveDate`,
      query: {
        employee: idEmployee.toString(),
        date: dateValue.toString(),
      },
      data: day.toJSON ? day.toJSON() : day,
    });

    return await this.request(request);
  }

  /**
   * Revert a roster date for an employee back to default hours.
   * GET /api/v2/roster/revertDate?employee={id}&date={date}
   */
  async revertDate(
    idEmployee: number | string,
    date: any
  ): Promise<ApiResponse<any>> {
    const dateValue = date?.value ?? date;
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/revertDate`,
      query: {
        employee: idEmployee.toString(),
        date: dateValue.toString(),
      },
    });

    return await this.request(request);
  }

  /**
   * Move an employee break to a new time/date/employee.
   * PUT /api/v2/roster/moveBreak
   */
  async moveBreak(
    employeeBreak: any,
    fromDate: number,
    toDate: number,
    employee: Employee,
    time: number,
    duration: number,
    label: string
  ): Promise<ApiResponse<EmployeeBreak>> {
    const breakData: Record<string, any> = {
      time: employeeBreak.time,
      duration: employeeBreak.duration,
      label: employeeBreak.label,
    };

    if (employeeBreak.idEmployeeBreak) {
      breakData.idEmployeeBreak = employeeBreak.idEmployeeBreak;
    } else {
      breakData.idEmployeeDefaultBreak = employeeBreak.idEmployeeDefaultBreak;
    }

    const data = {
      break: breakData,
      fromDate,
      toDate,
      time,
      duration,
      label,
      employee: employee.toJSON ? employee.toJSON() : employee,
    };

    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/moveBreak`,
      data,
    });

    return await this.requestItem<EmployeeBreak>(request, EmployeeBreak);
  }

  /**
   * Create a new employee break.
   * PUT /api/v2/roster/newBreak
   */
  async newBreak(
    date: number,
    employee: Employee,
    time: number,
    duration: number,
    label: string
  ): Promise<ApiResponse<EmployeeBreak>> {
    const data = {
      date,
      time,
      duration,
      label,
      employee: employee.toJSON ? employee.toJSON() : employee,
    };

    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/newBreak`,
      data,
    });

    return await this.requestItem<EmployeeBreak>(request, EmployeeBreak);
  }

  /**
   * Delete an employee break.
   * PUT /api/v2/roster/deleteBreak
   */
  async deleteBreak(
    employeeBreak: any,
    date: number
  ): Promise<boolean> {
    const data = {
      date,
      break: employeeBreak.toJSON ? employeeBreak.toJSON() : employeeBreak,
    };

    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/deleteBreak`,
      data,
    });

    return await this.requestSuccess(request);
  }

  /**
   * Fetch business hours configuration.
   * GET /api/v2/roster/businessHours
   */
  async businessHours(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/businessHours`,
    });

    return await this.request(request);
  }

  /**
   * Enable business hours (creates business hours employee).
   * POST /api/v2/roster/businessHours
   */
  async enableBusinessHours(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/businessHours`,
    });

    return await this.request(request);
  }

  /**
   * Fetch upcoming special dates (overrides) for an employee.
   * POST /api/v2/roster/upcomingSpecialDates
   */
  async upcomingSpecialDates(employee: { idEmployee: number | string }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/upcomingSpecialDates`,
      data: { employee },
    });

    return await this.request(request);
  }
}
